<script lang="ts">
	import { untrack } from "svelte";
	import * as Card from "#lib/web-ui/ui/card/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import XIcon from "@lucide/svelte/icons/x";
	import MailIcon from "@lucide/svelte/icons/mail";
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import { exitIntentNewsletterFormSchema } from "@fergmar/contracts/public/forms/newsletter-signup-popup.schema";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { toast } from "svelte-sonner";
	import { isMobile } from "@fergmar/ui/is-mobile.svelte";

	let { listId }: { listId: string } = $props();

	const SESSION_KEY = "fhb-exit-popup-seen";
	const SCROLL_DEPTH_RATIO = 0.5;
	const MIN_SCROLLED_BEFORE_EXIT = 400;
	const EXIT_JUMP_PX = 300;

	let scrollY = $state(0);
	let innerHeight = $state(0);
	let innerWidth = $state(0);
	let maxScrollY = $state(0);
	let shown = $state(false);
	let confirmed = $state(false);
	let seen = $state(false);
	let previouslyFocused: HTMLElement | null = null;
	let emailInputEl = $state<HTMLInputElement | null>(null);

	const docHeight = $derived(innerWidth > 0 ? document.documentElement.scrollHeight : 0);
	const hitScrollDepth = $derived(
		docHeight > 0 && scrollY + innerHeight >= docHeight * SCROLL_DEPTH_RATIO
	);
	const hitExitJump = $derived(
		maxScrollY >= MIN_SCROLLED_BEFORE_EXIT && maxScrollY - scrollY >= EXIT_JUMP_PX
	);

	function pushToDataLayer(eventName: string) {
		if (typeof window === "undefined" || !window.dataLayer) return;
		window.dataLayer.push({
			event: eventName,
			formId: "exit_intent_newsletter",
			pagePath: window.location.pathname
		});
	}

	function markSeen() {
		try {
			sessionStorage.setItem(SESSION_KEY, "true");
		} catch {
			// sessionStorage unavailable — accept re-showing
		}
		seen = true;
	}

	function triggerShow() {
		shown = true;
		markSeen();
		pushToDataLayer("exit_intent_popup_shown");
	}

	function dismiss() {
		if (shown && !confirmed) pushToDataLayer("exit_intent_popup_dismissed");
		shown = false;
		confirmed = false;
	}

	const form = superForm(defaults(zod4(exitIntentNewsletterFormSchema)), {
		id: "exit-intent-newsletter",
		SPA: true,
		resetForm: true,
		validators: zod4(exitIntentNewsletterFormSchema),
		async onUpdate({ form }) {
			if (!form.valid) return;

			try {
				await trpcBrowser.public.forms.submitNewsletterSignup.mutate({
					email: form.data.email,
					firstName: form.data.firstName.trim(),
					lastName: form.data.lastName.trim(),
					constantContactListId: listId
				});
			} catch (e) {
				toast.error(
					e instanceof Error ? e.message : "Something went wrong. Please try again."
				);
				return;
			}

			confirmed = true;
			pushToDataLayer("exit_intent_popup_submit");
			if (typeof window !== "undefined") window.fbq?.("track", "Lead");
			setTimeout(() => {
				shown = false;
				confirmed = false;
			}, 3000);
		}
	});

	const { form: formData, enhance, errors, submitting } = form;

	$effect(() => {
		try {
			seen = sessionStorage.getItem(SESSION_KEY) === "true";
		} catch {
			seen = false;
		}
	});

	$effect(() => {
		if (scrollY > untrack(() => maxScrollY)) maxScrollY = scrollY;
	});

	$effect(() => {
		if (shown || seen || !isMobile.value) return;
		if (hitScrollDepth || hitExitJump) triggerShow();
	});

	$effect(() => {
		if (!shown) return;

		previouslyFocused = document.activeElement as HTMLElement | null;
		queueMicrotask(() => emailInputEl?.focus());

		function onKeydown(e: KeyboardEvent) {
			if (e.key === "Escape") dismiss();
		}

		document.addEventListener("keydown", onKeydown);

		return () => {
			document.removeEventListener("keydown", onKeydown);
			previouslyFocused?.focus?.();
			previouslyFocused = null;
		};
	});
</script>

<svelte:window bind:scrollY bind:innerHeight bind:innerWidth />

{#if shown}
	<div class="fixed inset-0 z-50 bg-black/50" onclick={dismiss} role="presentation">
		<div
			class="fixed top-1/2 left-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 p-4"
			onclick={(e) => e.stopPropagation()}
			role="presentation"
		>
			<Card.Card
				class="border-2 shadow-lg"
				role="dialog"
				aria-modal="true"
				aria-labelledby="exit-intent-title"
				aria-describedby="exit-intent-sub"
			>
				<Card.CardHeader class="pb-2">
					<div class="flex items-start justify-between gap-2">
						<div class="flex items-center gap-2">
							<MailIcon class="text-primary h-5 w-5 shrink-0" />
							<Card.CardTitle
								id="exit-intent-title"
								class="text-foreground text-xl leading-tight"
							>
								Not ready to sell? No problem.
							</Card.CardTitle>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onclick={dismiss}
							class="h-8 w-8 shrink-0"
							aria-label="Close"
						>
							<XIcon class="h-4 w-4" />
						</Button>
					</div>
					<Card.CardDescription id="exit-intent-sub" class="text-muted-foreground pt-2">
						Stay in the loop on what's happening in the Crossroads market. Local
						insights, market updates, and expert guidance delivered to your inbox. So
						when the time is right, you'll be ready.
					</Card.CardDescription>
				</Card.CardHeader>

				<Card.CardContent>
					{#if confirmed}
						<p class="text-muted-foreground text-sm">
							You're in! We'll keep you up to date on the local market and walk you
							through everything you need to know about real estate in the Crossroads
							region. We're here when you're ready.
						</p>
					{:else}
						<form use:enhance method="POST" class="space-y-3">
							<div>
								<Input
									bind:ref={emailInputEl}
									name="email"
									type="email"
									bind:value={$formData.email}
									autocomplete="email"
									placeholder="Email address"
									aria-label="Email address"
									class="w-full"
									required
								/>
								{#if $errors.email}
									<p class="text-destructive mt-1 text-xs">{$errors.email}</p>
								{/if}
							</div>

							<div class="flex gap-2">
								<div class="flex-1">
									<Input
										name="firstName"
										type="text"
										bind:value={$formData.firstName}
										autocomplete="given-name"
										placeholder="First name"
										aria-label="First name"
										class="w-full"
										required
									/>
									{#if $errors.firstName}
										<p class="text-destructive mt-1 text-xs">
											{$errors.firstName}
										</p>
									{/if}
								</div>
								<div class="flex-1">
									<Input
										name="lastName"
										type="text"
										bind:value={$formData.lastName}
										autocomplete="family-name"
										placeholder="Last name"
										aria-label="Last name"
										class="w-full"
										required
									/>
									{#if $errors.lastName}
										<p class="text-destructive mt-1 text-xs">
											{$errors.lastName}
										</p>
									{/if}
								</div>
							</div>

							<Button type="submit" class="w-full" disabled={$submitting}>
								{#if $submitting}
									<div
										class="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
									></div>
									Subscribing...
								{:else}
									Keep Me in the Loop
								{/if}
							</Button>
						</form>
					{/if}
				</Card.CardContent>
			</Card.Card>
		</div>
	</div>
{/if}
